import React, { useState, ReactNode } from "react";
import styled from "styled-components";
import { Theme } from "../atoms/theme";

interface TabProps {
  label: string;
  children: ReactNode;
}

interface TabsProps {
  children: React.ReactElement<TabProps>[];
  defaultTab?: string;
}

const TabsContainer = styled.div`
  width: 100%;
`;

const TabHeader = styled.div`
  display: flex;
  width: fit-content;
  background: ${Theme.colors.neutrals_500};
  border-radius: ${Theme.radii.corner_radius_l};
  padding: 0.25rem;
  gap: 0.25rem;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  background: ${({ isActive }) =>
    isActive ? Theme.colors.primaries_000 : Theme.colors.neutrals_500};
  color: ${({ isActive }) =>
    isActive ? Theme.colors.neutrals_500 : Theme.colors.neutrals_000};
  border-radius: ${Theme.radii.corner_radius_m};
  padding: 0.25rem 0.75rem;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ isActive }) =>
      isActive ? Theme.colors.primaries_000 : Theme.colors.neutrals_400};
    color: ${({ isActive }) =>
      isActive ? Theme.colors.neutrals_500 : Theme.colors.neutrals_000};
  }

  &:focus {
    outline: none;
  }
`;

const TabContent = styled.div`
  width: 100%;
`;

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

export const Tabs: React.FC<TabsProps> = ({ children, defaultTab }) => {
  const [activeTab, setActiveTab] = useState(() => {
    if (defaultTab) {
      const defaultIndex = children.findIndex(
        (child) => child.props.label === defaultTab
      );
      return defaultIndex !== -1 ? defaultIndex : 0;
    }
    return 0;
  });

  const handleTabClick = (index: number) => {
    setActiveTab(index);
  };

  return (
    <TabsContainer>
      <TabHeader>
        {children.map((tab, index) => (
          <TabButton
            key={index}
            isActive={activeTab === index}
            onClick={() => handleTabClick(index)}
          >
            {tab.props.label}
          </TabButton>
        ))}
      </TabHeader>
      <TabContent>{children[activeTab]}</TabContent>
    </TabsContainer>
  );
};
